import React, { useState } from 'react';
import { Check, Edit2, Loader, Plus, X, X as XIcon } from 'react-feather';

import { useAddressMutations } from '@/Users/hooks/useAddressMutations';
import { EditAddressModalProps, UserAddress } from '@/Users/types/userTypes';

const EditAddressModal: React.FC<EditAddressModalProps> = ({
  onClose,
  addressList,
  userUuid,
}) => {
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editFormData, setEditFormData] = useState({
    postalCode: '',
    addressDetail: '',
  });
  const [newAddressData, setNewAddressData] = useState({
    postalCode: '',
    addressDetail: '',
  });

  const { addAddress, updateAddress, isAddingAddress, isUpdatingAddress } =
    useAddressMutations(userUuid);

  const handleEditClick = (address: UserAddress) => {
    setEditingAddressId(address.addressUuid);
    setEditFormData({
      postalCode: address.postalCode || '',
      addressDetail: address.addressDetail,
    });
    setIsAddingNew(false);
  };

  const handleCancelEdit = () => {
    setEditingAddressId(null);
    setEditFormData({ postalCode: '', addressDetail: '' });
  };

  const handleSaveEdit = () => {
    if (!editingAddressId) return;

    if (!editFormData.addressDetail.trim()) {
      alert('Please fill in the address detail');
      return;
    }

    // postalCode가 비어있으면 빈 문자열로 설정
    const payload: { postalCode: string; addressDetail: string } = {
      postalCode: editFormData.postalCode.trim() || '',
      addressDetail: editFormData.addressDetail.trim(),
    };

    updateAddress(editingAddressId, payload);

    setEditingAddressId(null);
    setEditFormData({ postalCode: '', addressDetail: '' });
  };

  const handleAddNewClick = () => {
    if (addressList.length >= 5) {
      alert('Maximum 5 addresses allowed');
      return;
    }
    setIsAddingNew(true);
    setEditingAddressId(null);
    setNewAddressData({ postalCode: '', addressDetail: '' });
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewAddressData({ postalCode: '', addressDetail: '' });
  };

  const handleSaveNew = () => {
    if (!newAddressData.addressDetail.trim()) {
      alert('Please fill in the address detail');
      return;
    }

    // postalCode가 비어있으면 payload에서 제외
    const payload: { postalCode?: string; addressDetail: string } = {
      addressDetail: newAddressData.addressDetail.trim(),
    };

    if (newAddressData.postalCode.trim()) {
      payload.postalCode = newAddressData.postalCode.trim();
    }

    addAddress(payload);

    setIsAddingNew(false);
    setNewAddressData({ postalCode: '', addressDetail: '' });
  };

  const isLoading = isAddingAddress || isUpdatingAddress;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-address-modal-title"
    >
      <div className="max-h-[80vh] w-[32.5rem] overflow-y-auto rounded-lg bg-white text-center">
        <div className="sticky top-0 flex w-full items-center justify-between border-b border-[#eaeaea] bg-white p-6">
          <h3 className="text-xl font-medium" id="edit-address-modal-title">
            Address Management
          </h3>
          <div className="flex items-center gap-2">
            {!isAddingNew && addressList.length < 5 && (
              <button
                type="button"
                className="cursor-pointer rounded bg-transparent p-1 hover:bg-[#f1f1f1]"
                onClick={handleAddNewClick}
                aria-label="Add new address"
                disabled={isLoading}
              >
                <Plus size={16} className="text-[#666666]" aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="cursor-pointer rounded bg-transparent p-1 hover:bg-[#f1f1f1]"
              onClick={() => onClose(false)}
              aria-label="Close address modal"
              disabled={isLoading}
            >
              <X size={20} className="text-[#707070]" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="p-6 text-left">
          {isLoading && (
            <div className="mb-4 flex items-center justify-center rounded bg-[#f9f9f9] p-4">
              <Loader size={16} className="mr-2 animate-spin text-[#3e3e3e]" />
              <span className="text-sm text-[#666]">
                {isAddingAddress ? 'Adding address...' : 'Updating address...'}
              </span>
            </div>
          )}

          {/* Add New Address Form */}
          {isAddingNew && (
            <div className="mb-6 rounded border border-[#888888] bg-[#f9f9f9] p-4">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-medium text-[#1d1d1d]">
                  Add New Address
                </h4>
                <button
                  type="button"
                  className="cursor-pointer rounded bg-transparent p-1 hover:bg-[#e0e0e0]"
                  onClick={handleCancelAdd}
                  aria-label="Cancel add address"
                  disabled={isLoading}
                >
                  <XIcon size={14} className="text-[#666]" />
                </button>
              </div>

              <div className="flex flex-col gap-3">
                <div>
                  <label
                    htmlFor="newPostalCode"
                    className="mb-1 block text-xs font-medium text-[#666]"
                  >
                    Postal Code (Optional)
                  </label>
                  <input
                    id="newPostalCode"
                    type="text"
                    value={newAddressData.postalCode}
                    onChange={(e) =>
                      setNewAddressData((prev) => ({
                        ...prev,
                        postalCode: e.target.value,
                      }))
                    }
                    className="w-full rounded border border-[#cecece] px-3 py-2 text-sm text-[#444] focus:border-[#3e3e3e] focus:outline-none"
                    placeholder="Enter postal code (optional)"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label
                    htmlFor="newAddressDetail"
                    className="mb-1 block text-xs font-medium text-[#666]"
                  >
                    Address Detail *
                  </label>
                  <input
                    id="newAddressDetail"
                    type="text"
                    value={newAddressData.addressDetail}
                    onChange={(e) =>
                      setNewAddressData((prev) => ({
                        ...prev,
                        addressDetail: e.target.value,
                      }))
                    }
                    className="w-full rounded border border-[#cecece] px-3 py-2 text-sm text-[#444] focus:border-[#3e3e3e] focus:outline-none"
                    placeholder="Enter address detail"
                    disabled={isLoading}
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="flex cursor-pointer items-center rounded bg-[#3e3e3e] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#222] disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={handleSaveNew}
                    disabled={isLoading}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Address List */}
          {addressList.length > 0 ? (
            <div className="flex flex-col gap-4">
              {addressList.map((address, index) => (
                <div
                  key={address.addressUuid}
                  className={`rounded border p-4 ${
                    editingAddressId === address.addressUuid
                      ? 'border-[#3e3e3e] bg-[#f9f9f9]'
                      : 'border-[#cecece] bg-white'
                  }`}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-medium text-[#1d1d1d]">
                      Address {index + 1}
                      {address.isDefault && (
                        <span className="ml-2 rounded bg-[#3e3e3e] px-2 py-1 text-xs text-white">
                          Default
                        </span>
                      )}
                    </h4>
                    {editingAddressId === address.addressUuid ? (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="cursor-pointer rounded bg-transparent p-1 hover:bg-[#e0e0e0]"
                          onClick={handleSaveEdit}
                          aria-label="Save address"
                          disabled={isLoading}
                        >
                          <Check size={14} className="text-[#3e3e3e]" />
                        </button>
                        <button
                          type="button"
                          className="cursor-pointer rounded bg-transparent p-1 hover:bg-[#e0e0e0]"
                          onClick={handleCancelEdit}
                          aria-label="Cancel edit"
                          disabled={isLoading}
                        >
                          <XIcon size={14} className="text-[#666]" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="cursor-pointer rounded bg-transparent p-1 hover:bg-[#f0f0f0]"
                        onClick={() => handleEditClick(address)}
                        aria-label="Edit address"
                        disabled={isLoading}
                      >
                        <Edit2 size={14} className="text-[#666666]" />
                      </button>
                    )}
                  </div>

                  {editingAddressId === address.addressUuid ? (
                    <div className="flex flex-col gap-3">
                      <div>
                        <label
                          htmlFor="editPostalCode"
                          className="mb-1 block text-xs font-medium text-[#666]"
                        >
                          Postal Code (Optional)
                        </label>
                        <input
                          id="editPostalCode"
                          type="text"
                          value={editFormData.postalCode}
                          onChange={(e) =>
                            setEditFormData((prev) => ({
                              ...prev,
                              postalCode: e.target.value,
                            }))
                          }
                          className="w-full rounded border border-[#cecece] px-3 py-2 text-sm text-[#444] focus:border-[#3e3e3e] focus:outline-none"
                          placeholder="Enter postal code (optional)"
                          disabled={isLoading}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="editAddressDetail"
                          className="mb-1 block text-xs font-medium text-[#666]"
                        >
                          Address Detail *
                        </label>
                        <input
                          id="editAddressDetail"
                          type="text"
                          value={editFormData.addressDetail}
                          onChange={(e) =>
                            setEditFormData((prev) => ({
                              ...prev,
                              addressDetail: e.target.value,
                            }))
                          }
                          className="w-full rounded border border-[#cecece] px-3 py-2 text-sm text-[#444] focus:border-[#3e3e3e] focus:outline-none"
                          placeholder="Enter address detail"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 text-sm text-[#444]">
                      {address.companyName && (
                        <div className="flex justify-between">
                          <span className="font-medium text-[#666]">
                            Company:
                          </span>
                          <span>{address.companyName}</span>
                        </div>
                      )}

                      {address.postalCode && (
                        <div className="flex justify-between">
                          <span className="font-medium text-[#666]">
                            Postal Code:
                          </span>
                          <span>{address.postalCode}</span>
                        </div>
                      )}

                      <div className="flex justify-between">
                        <span className="font-medium text-[#666]">
                          Address:
                        </span>
                        <span className="max-w-[200px] text-right">
                          {address.province && `${address.province} `}
                          {address.city && `${address.city} `}
                          {address.addressDetail}
                        </span>
                      </div>

                      {address.nationCode2 && (
                        <div className="flex justify-between">
                          <span className="font-medium text-[#666]">
                            Country:
                          </span>
                          <span>{address.nationCode2}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <p className="mb-4 text-sm text-[#666]">No addresses found</p>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <div className="text-xs text-[#888]">
              {addressList.length}/5 addresses used
            </div>
            <button
              type="button"
              className="h-8 cursor-pointer rounded bg-[#666] px-4 text-sm font-medium text-white hover:bg-[#444] disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Close address management"
              onClick={() => onClose(false)}
              disabled={isLoading}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAddressModal;
